import { useMemo } from 'react';
import { ChevronDownIcon, Mails } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@rocco/components/dropdown-menu';
import type { MeUser } from '~/server/types';
import type { InboxCompanyProfessional } from '../../types';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';

/**
 * Dropdown to filter company inbox enquiry conversations by network branch.
 * @see {@link ChatInboxDrawer}
 */
export function ChatInboxBranchSwitcher({
    className,
    user,
    professionalID,
    temporaryCompanyInbox = null,
    branchProfessionalID,
    onBranchChange,
}: {
    className?: string;
    user: MeUser;
    professionalID: number | null;
    temporaryCompanyInbox?: InboxCompanyProfessional | null;
    branchProfessionalID: number | null;
    onBranchChange?: (branchProfessionalID: number | null) => void;
}) {
    const options = useMemo(() => {
        const professional =
            user.Professionals.find((item) => item.ID === professionalID) ??
            (temporaryCompanyInbox?.ID === professionalID
                ? temporaryCompanyInbox
                : null);
        const network = professional?.Network;
        if (!network || !network.participants.nodes.length) {
            return null;
        }
        return [
            ALL_LINKED_PROFILES_OPTION,
            {
                label: professional.Title,
                value: professional.ID,
                professional,
            },
            ...network.participants.nodes.map(({ identity }) => ({
                label: identity.Title,
                value: identity.ID,
                professional: identity,
            })),
        ];
    }, [professionalID, user.Professionals, temporaryCompanyInbox]);
    const selectedOption =
        options?.find((option) => option.value === branchProfessionalID) ??
        ALL_LINKED_PROFILES_OPTION;

    if (!options) {
        return null;
    }

    return (
        <div className={cn(className)}>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className="flex h-12 w-full items-center gap-3 rounded-full px-4 text-left hover:bg-gray-200"
                    >
                        <span className="min-w-0 flex-1 truncate text-4 font-medium">
                            {selectedOption.label}
                        </span>
                        <ChevronDownIcon className="size-4 shrink-0 text-black/70" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className="w-[calc(var(--radix-dropdown-menu-trigger-width))]"
                    side="bottom"
                    align="start"
                >
                    {options.map((option) => (
                        <DropdownMenuItem
                            key={option.value ?? 0}
                            className="flex items-center gap-3 focus:bg-black/10"
                            onClick={() => onBranchChange?.(option.value)}
                        >
                            {option.value === null ? (
                                <span className="grid size-8 shrink-0 place-items-center rounded-full bg-gray-100">
                                    <Mails className="size-4" aria-hidden />
                                </span>
                            ) : (
                                <ParticipantAvatar
                                    className="size-8"
                                    fallbackClassName="text-4!"
                                    participant={option.professional}
                                />
                            )}
                            <span className="min-w-0 flex-1 truncate">
                                {option.label}
                            </span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

const ALL_LINKED_PROFILES_OPTION = {
    label: 'All linked profiles',
    value: null,
} as const;
